/* eslint-disable max-lines-per-function */

import { useDemoVariantResource } from '@dreryk/demo-variant-frontend';
import {
   CalendarIcon,
   CloudIcon,
   GroupIcon,
   MainMenu,
   PaymentsIcon,
   PrescriptionVisitIcon,
   StatsBarIcon,
   StethoscopeIcon,
   SupportIcon,
   TwoUsersIcon,
} from '@dreryk/react-components';

import { createMedicalRoleVisitor, useCurrentPractitionerGlobalState } from '~libs.shared.account';
import { protectFeature } from '~libs.shared.system';
import { DashboardNavigationItem } from '~legacy.dashboard';
import { getStatisticsPath } from '~legacy.statistics';
import { dispatchVisitDetailsCopyEvent } from '~legacy.visit';
import {
   AiAssistantMainMenuTileItem,
   AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback,
   useAiAssistantAvailable,
} from '~libs.desktop.ai-assistant';
import { getGroupSessionsPath } from '~libs.desktop.group-sessions';
import { patientPaths, visitPaths } from '~libs.desktop.shared.paths';
import { VideoConsultationMainMenuTileItem } from '~libs.desktop.video-consultation';

import { CurrentHealthcareProviderMainMenuIndicatorItem } from './features/currentHealthcareProviderModuleIndicator/CurrentHealthcareProviderMainMenuIndicatorItem';
import { StyledPaymentsAndInvoicesIcon } from './features/paymentsAndInvoices/PaymentsAndInvoicesIcon.styled';
import { getPaymentsAndInvoicesPath } from './features/paymentsAndInvoices/PaymentsAndInvoicesPanel.utils';
import { appTranslationKeys } from './translations';

export const PHARMINDEX_INTEGRATIONS_PATH = '/pharmindexIntegrations';
const HELP_CENTER_LINK = 'https://centrumpomocy.egabinet.dreryk.pl/';

const handleAcceptSummary: AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback = (result) => {
   dispatchVisitDetailsCopyEvent({
      interview: result.interview,
      subjectiveAssessment: result.examination,
      recommendations: result.recommendations,
   });
};

export const AppMainMenu = (): JSX.Element => {
   const { getCurrentPractitioner } = useCurrentPractitionerGlobalState();
   const currentPractitioner = getCurrentPractitioner();

   const prescriptionVisitsMenuItem = (
      <MainMenu.NavigationItem
         testId={'prescriptionVisits'}
         path={visitPaths.getPrescriptionVisitsPath()}
         additionalSelectionPaths={[visitPaths.PRESCRIPTION_VISIT_PATH, visitPaths.PRESCRIPTION_VISITS_PATH]}
         icon={<PrescriptionVisitIcon />}
         label={appTranslationKeys.mainMenu.prescriptionVisits}
      />
   );

   const prescriptionVisitsNavigationItem = protectFeature(
      'searchingAppointments',
      protectFeature('updatingProviderData') || protectFeature('updatingReceptionistPersonalData')
         ? prescriptionVisitsMenuItem
         : createMedicalRoleVisitor(currentPractitioner)<JSX.Element | null>({
              visitDoctor: () => prescriptionVisitsMenuItem,
              visitParamedic: () => null,
              visitUnknown: () => null,
           }),
      null
   );

   const accessToGroupSessions =
      protectFeature('conductingGroupSessions') ||
      protectFeature('managingGroupSessions') ||
      // TODO DREGAB-27876 Usunięcie flagi experimental
      protectFeature('experimental');

   const aiAssistantAvailable = useAiAssistantAvailable();

   return (
      <MainMenu
         testId={'mainMenu'}
         id={'mainMenu'}
         navigation={
            <MainMenu.Navigation>
               <DashboardNavigationItem />
               {protectFeature(
                  'searchingAppointments',
                  <MainMenu.NavigationItem
                     testId={'visits'}
                     path={visitPaths.getAppointmentsListPath()}
                     additionalSelectionPaths={visitPaths.getVisitPaths()}
                     icon={<StethoscopeIcon />}
                     label={appTranslationKeys.mainMenu.visits}
                  />
               )}
               {prescriptionVisitsNavigationItem}
               {protectFeature(
                  'searchingAppointments',
                  <MainMenu.NavigationItem
                     testId={'calendar'}
                     path={visitPaths.CALENDAR_PATH}
                     icon={<CalendarIcon />}
                     label={appTranslationKeys.mainMenu.calendar}
                  />
               )}
               {protectFeature(
                  'creatingPatients',
                  <MainMenu.NavigationItem
                     testId={'patients'}
                     path={patientPaths.getEntryPath()}
                     icon={<TwoUsersIcon />}
                     label={appTranslationKeys.mainMenu.patients}
                  />
               )}
               {accessToGroupSessions ? (
                  <MainMenu.NavigationItem
                     testId={'groupSessions'}
                     path={getGroupSessionsPath()}
                     icon={<GroupIcon />}
                     label={appTranslationKeys.mainMenu.groupSessions}
                  />
               ) : null}
               {protectFeature(
                  ['searchingPrivateMedicalServices', 'creatingInvoices'],
                  <MainMenu.NavigationItem
                     testId={'paymentsAndInvoices'}
                     path={getPaymentsAndInvoicesPath()}
                     icon={
                        <StyledPaymentsAndInvoicesIcon>
                           <PaymentsIcon />
                        </StyledPaymentsAndInvoicesIcon>
                     }
                     label={appTranslationKeys.mainMenu.paymentsAndInvoices}
                  />
               )}
               {protectFeature(
                  'creatingStatistics',
                  <MainMenu.NavigationItem
                     testId={'statistics'}
                     path={getStatisticsPath()}
                     icon={<StatsBarIcon />}
                     label={appTranslationKeys.mainMenu.statistics}
                  />
               )}
               {protectFeature(
                  'pharmindexIntegration',
                  <MainMenu.NavigationItem
                     testId={'pharmindex'}
                     path={PHARMINDEX_INTEGRATIONS_PATH}
                     icon={<CloudIcon />}
                     label={appTranslationKeys.mainMenu.pharmindex}
                  />
               )}
            </MainMenu.Navigation>
         }
         footer={useDemoVariantResource(
            <MainMenu.Footer>
               {aiAssistantAvailable ? <AiAssistantMainMenuTileItem onAcceptSummary={handleAcceptSummary} /> : null}
               {protectFeature(
                  'performingVideoConsultations',
                  <VideoConsultationMainMenuTileItem />,

                  null
               )}
               <MainMenu.LinkItem
                  testId={'helpCenter'}
                  link={HELP_CENTER_LINK}
                  icon={<SupportIcon />}
                  label={appTranslationKeys.mainMenu.helpCenter}
               />
               <CurrentHealthcareProviderMainMenuIndicatorItem />
            </MainMenu.Footer>,
            undefined
         )}
      />
   );
};
